import React, { useState } from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Upload, UploadOnAddEvent, UploadFileInfo } from "@progress/kendo-react-upload";
import { Button } from "@progress/kendo-react-buttons";
import { ProgressBar } from "@progress/kendo-react-progressbars";
import { SvgIcon } from "@progress/kendo-react-common";
import { cancelIcon, xIcon } from "@progress/kendo-svg-icons";
import { useInjection } from "@presentation/components/dependencyInjection";
import { UploadLibraryService } from "@application/useCases/library/UploadLibraryService";
import { useNotifications } from "@presentation/components/notifications";
import { useUploadStore } from "@presentation/stores/UploadStore";
import { getUploadStatusColor, getUploadStatusLabel, formatUploadDestination } from "@presentation/components/inventory/components/uploadStatusPresentation";
import { useUploadDestinationPicker } from "@presentation/components/uploadDestination/useUploadDestinationPicker";
import { PathTypeSchema } from "@domain/schemas/BI";
import { UploadDestinationField } from "@presentation/components/uploadDestination/UploadDestinationField";
import { useProjectPermission } from "@presentation/components/helpers/usePermissionCheck";
import { getUploadErrorMessage } from "@presentation/common/ErrorHelpers";
import { ProjectActions } from "@domain/schemas/workspace";

interface UploadLibraryDialogProperties {
  projectId: string;
  onClose: () => void;
  onUploadSuccess?: () => void;
}

const isFinished = (status: string) => ["completed", "failed", "cancelled"].includes(status);

export const UploadLibraryDialog: React.FC<UploadLibraryDialogProperties> = ({ projectId, onClose, onUploadSuccess }) => {
  const [files, setFiles] = useState<UploadFileInfo[]>([]);
  const uploadLibraryService = useInjection(UploadLibraryService);
  const { pushNotification } = useNotifications();
  const { activeUploads, cancelUpload, removeUpload } = useUploadStore();
  const { dialog: destinationDialog, pickDestination, destination, error: destinationError, isChoosing } = useUploadDestinationPicker(PathTypeSchema.Libraries, projectId);
  const { allowed } = useProjectPermission(projectId, ProjectActions.TriggerJob);
  const [errorMessage, setErrorMessage] = useState("");

  const libraryUploads = Object.values(activeUploads)
    .filter((u) => u.type === "library" && u.projectId === projectId)
    .toSorted((a, b) => {
      // Primary sort: Progress (descending)
      const progressDiff = b.progress.percent - a.progress.percent;
      if (progressDiff !== 0) return progressDiff;
      // Secondary sort: ID (descending) as a stable fallback
      return b.id.localeCompare(a.id);
    });

  const onAdd = (event: UploadOnAddEvent) => {
    setFiles(event.affectedFiles);
  };

  const handleUpload = async () => {
    if (!allowed || isChoosing || !destination || files.length === 0) return;
    setErrorMessage("");

    const filesToUpload = [...files];
    const fileObjects = filesToUpload.map((fileInfo) => fileInfo.getRawFile?.()).filter((file): file is File => file instanceof File);
    if (fileObjects.length === 0) return;

    // Clear files from the input immediately so user sees them move to the list below
    setFiles([]);

    try {
      const results = await Promise.allSettled(fileObjects.map((file) => uploadLibraryService.uploadFile(file, projectId, destination.folder, destination.rootPath)));
      const failures = results.filter((result) => result.status === "rejected");
      if (results.some((result) => result.status === "fulfilled")) onUploadSuccess?.();
      if (failures.length > 0) {
        setErrorMessage(failures.map((result) => getUploadErrorMessage(result.reason)).join("\n"));
      } else {
        pushNotification("Libraries were successfully uploaded.", "success");
        onClose();
      }
    } catch (error: unknown) {
      setErrorMessage(getUploadErrorMessage(error));
    }
  };

  return (
    <Dialog title="Upload Libraries" onClose={onClose} width={600}>
      <div style={{ padding: "20px" }}>
        <div style={{ marginBottom: "20px" }}>
          <p>Select .kit or .speclib files to upload.</p>
          <Upload
            batch={false}
            multiple={true}
            disabled={!allowed || isChoosing}
            autoUpload={false}
            files={files}
            onAdd={onAdd}
            onRemove={(e) => {
              setFiles(e.newState);
            }}
            showActionButtons={false}
          />
          {!allowed && <p role="alert">You do not have permission to upload files in this project.</p>}
          <UploadDestinationField
            destination={destination}
            onChoose={() => void pickDestination(files.length === 1 ? files[0].name : `${files.length} files`)}
            disabled={!allowed || files.length === 0 || isChoosing}
            error={destinationError}
          />
          {errorMessage && (
            <p role="alert" style={{ color: "#b91c1c", whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
              {errorMessage}
            </p>
          )}
        </div>

        {libraryUploads.length > 0 && (
          <div style={{ marginBottom: "20px" }}>
            <label style={{ display: "block", marginBottom: "10px", fontWeight: "bold" }}>Upload Status:</label>
            <div style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #eee", padding: "10px", borderRadius: "4px" }}>
              {libraryUploads.map((upload) => (
                <div key={upload.id} style={{ marginBottom: "5px", paddingBottom: "5px", borderBottom: "1px solid #f5f5f5" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", fontSize: "0.85rem", marginBottom: "5px" }}>
                    <div style={{ flex: 1, marginRight: "10px", minWidth: 0 }}>
                      <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: "bold" }} title={upload.fileName}>
                        {upload.fileName}
                      </div>
                      <div style={{ overflowWrap: "anywhere" }}>Destination: {formatUploadDestination(upload.rootPath, upload.folder)}</div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                      <span style={{ color: getUploadStatusColor(upload.status), fontWeight: "bold" }}>{getUploadStatusLabel(upload)}</span>

                      {(upload.status === "uploading" || upload.status === "pending") && (
                        <Button fillMode="flat" title="Cancel Upload" onClick={() => cancelUpload(upload.id)}>
                          <SvgIcon icon={cancelIcon} />
                        </Button>
                      )}

                      {isFinished(upload.status) && (
                        <Button fillMode="flat" title="Remove from list" onClick={() => removeUpload(upload.id)}>
                          <SvgIcon icon={xIcon} />
                        </Button>
                      )}
                    </div>
                  </div>
                  {upload.status === "failed" && upload.error && (
                    <div role="alert" style={{ fontSize: "0.85rem", color: "#b91c1c", marginTop: "2px", overflowWrap: "anywhere", whiteSpace: "pre-wrap" }}>
                      Error: {upload.error}
                    </div>
                  )}
                  <ProgressBar value={upload.progress.percent} labelVisible={false} style={{ height: "5px", borderRadius: "3px", marginTop: "2px", marginBottom: "2px" }} />
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "10px" }}>
          <Button onClick={onClose}>Close</Button>
          <Button themeColor="primary" onClick={handleUpload} disabled={!allowed || !destination || files.length === 0 || isChoosing}>
            Start Upload
          </Button>
        </div>
      </div>
      {destinationDialog}
    </Dialog>
  );
};
